import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import toast from "react-hot-toast";

const MONTH_OPTIONS = [
  { month: 1, label: "January" },
  { month: 2, label: "February" },
  { month: 3, label: "March" },
  { month: 4, label: "April" },
  { month: 5, label: "May" },
  { month: 6, label: "June" },
  { month: 7, label: "July" },
  { month: 8, label: "August" },
  { month: 9, label: "September" },
  { month: 10, label: "October" },
  { month: 11, label: "November" },
  { month: 12, label: "December" },
];

const normalizeRole = (role) => String(role || "").trim().toLowerCase().replace(/[_\s]+/g, "-");

const getCurrentFiscalYear = () => {
  const now = new Date();
  const startYear = now.getUTCMonth() >= 7 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;
  return `${startYear}-${startYear + 1}`;
};

const isValidFiscalYear = (value) => {
  const normalized = String(value || "").trim();
  const match = normalized.match(/^(\d{4})-(\d{4})$/);
  if (!match) return false;
  return Number(match[2]) === Number(match[1]) + 1;
};

const normalizeMonths = (rows = []) => {
  const byMonth = new Map(
    Array.isArray(rows)
      ? rows
        .map((row) => [Number(row?.month), Number(row?.budgetAmount ?? 0)])
        .filter(([month]) => Number.isInteger(month) && month >= 1 && month <= 12)
      : []
  );

  return MONTH_OPTIONS.map(({ month, label }) => ({
    month,
    label,
    budgetAmount: byMonth.has(month) ? String(byMonth.get(month)) : "0",
  }));
};

const formatCurrency = (value) => `$${new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
}).format(Number(value || 0))}`;

async function resolveProfileInstanceUniqueId({ authState, headers, serverUrl }) {
  const pathMatch = window.location.pathname.match(/\/instance\/([^/]+)/);
  if (pathMatch?.[1]) return pathMatch[1];
  if (!authState?.id) return "";

  const response = await axios.get(
    `${serverUrl}/instances/byuserId/${authState.id}`,
    headers,
  );
  const instances = Array.isArray(response.data) ? response.data : [];
  if (!instances.length) return "";

  const preferredInstance = instances.find((instance) => Number(instance?.id) === Number(authState?.instanceId));
  return String(preferredInstance?.instanceUniqueId || instances[0]?.instanceUniqueId || "").trim();
}

export default function SalesBudgetProfilePanel({
  authState,
  targetUserId,
  targetUserRole,
  canManage = false,
}) {
  const serverUrl = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
  const normalizedTargetRole = normalizeRole(targetUserRole);
  const isRepProfile = ["client-rep", "order-rep"].includes(normalizedTargetRole);
  const isSelfProfile = Number(authState?.id) === Number(targetUserId);
  const canReadPanel = canManage || isSelfProfile;

  const [instanceUniqueId, setInstanceUniqueId] = useState("");
  const [instanceError, setInstanceError] = useState("");
  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());
  const [months, setMonths] = useState(() => normalizeMonths());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const annualTotal = useMemo(() => Number(months.reduce((sum, row) => {
    const value = Number(row?.budgetAmount ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0).toFixed(2)), [months]);

  useEffect(() => {
    let active = true;
    if (!isRepProfile || !canReadPanel) return () => {};

    const loadInstance = async () => {
      setInstanceError("");
      try {
        const resolvedSlug = await resolveProfileInstanceUniqueId({
          authState,
          headers,
          serverUrl,
        });
        if (!active) return;
        if (!resolvedSlug) {
          setInstanceUniqueId("");
          setInstanceError("Unable to resolve the current instance for Sales Budget targets.");
          return;
        }
        setInstanceUniqueId(resolvedSlug);
      } catch (err) {
        if (!active) return;
        setInstanceUniqueId("");
        setInstanceError("Unable to resolve the current instance for Sales Budget targets.");
      }
    };

    loadInstance();
    return () => {
      active = false;
    };
  }, [authState, canReadPanel, isRepProfile, serverUrl]);

  useEffect(() => {
    let active = true;
    if (!isRepProfile || !canReadPanel || !instanceUniqueId || !isValidFiscalYear(fiscalYear)) return () => {};

    const loadBudgets = async () => {
      setLoading(true);
      setError("");
      setSuccessMessage("");
      try {
        const response = await axios.get(
          `${serverUrl}/api/instances/${instanceUniqueId}/users/${targetUserId}/sales-budgets`,
          {
            ...headers,
            params: { fiscalYear },
          }
        );
        if (!active) return;
        setMonths(normalizeMonths(response.data?.months));
      } catch (err) {
        if (!active) return;
        const message = err?.response?.data?.message || "Failed to load sales budget targets.";
        setError(message);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadBudgets();
    return () => {
      active = false;
    };
  }, [canReadPanel, fiscalYear, instanceUniqueId, isRepProfile, serverUrl, targetUserId]);

  if (!isRepProfile || !canReadPanel) return null;

  const handleMonthChange = (month, value) => {
    setMonths((previous) => previous.map((entry) => (
      entry.month === month ? { ...entry, budgetAmount: value } : entry
    )));
  };

  const save = async () => {
    if (!canManage || !instanceUniqueId) return;
    if (!isValidFiscalYear(fiscalYear)) {
      setError("Fiscal year must use the YYYY-YYYY format with consecutive years.");
      return;
    }

    const payloadMonths = [];
    for (const row of months) {
      const budgetAmount = Number(row?.budgetAmount ?? 0);
      if (!Number.isFinite(budgetAmount) || budgetAmount < 0) {
        setError(`Enter a non-negative amount for ${row.label}.`);
        return;
      }
      payloadMonths.push({
        month: row.month,
        budgetAmount: Number(budgetAmount.toFixed(2)),
      });
    }

    setSaving(true);
    setError("");
    setSuccessMessage("");
    try {
      const response = await axios.put(
        `${serverUrl}/api/instances/${instanceUniqueId}/users/${targetUserId}/sales-budgets`,
        {
          fiscalYear,
          months: payloadMonths,
        },
        headers,
      );
      setMonths(normalizeMonths(response.data?.months));
      const message = response.data?.message || "Sales budget targets saved.";
      setSuccessMessage(message);
      toast.success(message);
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to save sales budget targets.";
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-db-widget-box">
      <div className="inner-widget-box">
        <div className="widget-heading">
          <h3>Sales Budget</h3>
        </div>
        <div className="widget-details">
          <span className="archive-statistics-label">
            Set this rep&apos;s monthly sales targets for reporting.
          </span>

          {!canManage ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Budget targets are managed by administrators and finance users.
            </Alert>
          ) : null}

          {instanceError ? (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {instanceError}
            </Alert>
          ) : null}

          <TextField
            label="Fiscal Year"
            value={fiscalYear}
            onChange={(e) => setFiscalYear(e.target.value)}
            size="small"
            helperText="Example: 2026-2027"
            error={Boolean(fiscalYear) && !isValidFiscalYear(fiscalYear)}
            sx={{ mt: 2, width: "100%", maxWidth: 240 }}
          />

          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading monthly budget targets...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(220px, 1fr))" },
                gap: 2,
                mt: 2,
              }}
            >
              {months.map((row) => (
                <TextField
                  key={row.month}
                  label={row.label}
                  type="number"
                  size="small"
                  value={row.budgetAmount}
                  onChange={(e) => handleMonthChange(row.month, e.target.value)}
                  disabled={!canManage || Boolean(instanceError)}
                  inputProps={{ min: 0, step: "0.01" }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Annual Total
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {formatCurrency(annualTotal)}
            </Typography>
          </Box>

          {successMessage ? <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert> : null}
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}

          {canManage ? (
            <Button
              className="archive-statistics-button"
              variant="contained"
              sx={{ mt: 2 }}
              onClick={save}
              disabled={saving || loading || !instanceUniqueId || !isValidFiscalYear(fiscalYear)}
            >
              {saving ? "Saving..." : "Save Sales Budget"}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
